import { logger } from '@heidi/runtime/logger';
import { Component, lazy, type ReactNode, Suspense, useSyncExternalStore } from 'react';

import type { EvidenceHomeAdPlacementProps } from './evidence-home-placement';
import type { EvidenceHomeSidebarAdPlacementProps } from './evidence-home-sidebar-placement';
import type { EvidenceLoadingAdPlacementProps } from './evidence-loading-placement';
import type { ScribeNoteGenAdPlacementProps } from './scribe-note-gen-placement';

/*
 * The placements published on `.`. A host renders one only once its product's
 * flag is on, so a placement and the SDK load then and not with the page.
 *
 * `React.lazy` rather than `next/dynamic`, which this zone may not import. It
 * keeps what `ssr: false` and an empty `loading` gave: nothing is drawn or
 * requested on the server or while hydrating, and a chunk that fails to load
 * leaves the slot empty rather than reaching an error boundary. So does a
 * placement that throws once drawn: a sponsored unit never takes down the
 * answer or the note it sits beside.
 */

const subscribe = () => () => {};

function EmptySlot() {
  return null;
}

const emptySlot = () => ({ default: EmptySlot });

class SlotBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    logger.error('[ads] placement failed to render', {
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function ClientOnly({ children }: { children: ReactNode }) {
  const isClient = useSyncExternalStore(
    subscribe,
    () => true,
    () => false
  );
  if (!isClient) return null;

  return (
    <SlotBoundary>
      <Suspense fallback={null}>{children}</Suspense>
    </SlotBoundary>
  );
}

const LazyEvidenceHome = lazy(() =>
  import('./evidence-home-placement').then(
    (mod) => ({ default: mod.EvidenceHomeAdPlacement }),
    emptySlot
  )
);

const LazyEvidenceHomeSidebar = lazy(() =>
  import('./evidence-home-sidebar-placement').then(
    (mod) => ({ default: mod.EvidenceHomeSidebarAdPlacement }),
    emptySlot
  )
);

const LazyEvidenceLoading = lazy(() =>
  import('./evidence-loading-placement').then(
    (mod) => ({ default: mod.EvidenceLoadingAdPlacement }),
    emptySlot
  )
);

const LazyScribeNoteGen = lazy(() =>
  import('./scribe-note-gen-placement').then(
    (mod) => ({ default: mod.ScribeNoteGenAdPlacement }),
    emptySlot
  )
);

export function EvidenceHomeAdPlacement(props: EvidenceHomeAdPlacementProps) {
  return (
    <ClientOnly>
      <LazyEvidenceHome {...props} />
    </ClientOnly>
  );
}

export function EvidenceHomeSidebarAdPlacement(
  props: EvidenceHomeSidebarAdPlacementProps
) {
  return (
    <ClientOnly>
      <LazyEvidenceHomeSidebar {...props} />
    </ClientOnly>
  );
}

export function EvidenceLoadingAdPlacement(props: EvidenceLoadingAdPlacementProps) {
  return (
    <ClientOnly>
      <LazyEvidenceLoading {...props} />
    </ClientOnly>
  );
}

export function ScribeNoteGenAdPlacement(props: ScribeNoteGenAdPlacementProps) {
  return (
    <ClientOnly>
      <LazyScribeNoteGen {...props} />
    </ClientOnly>
  );
}
